import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import Navbar from "../../components/Navbar";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import AOS from "aos";
import "aos/dist/aos.css";
import ReactPlayer from "react-player";
import PrimaryBtn from "../../components/PrimaryBtn";
import Slider from "react-slick";
import { useLocation } from "react-router-dom";
import { MdOutlineReplay } from "react-icons/md";
import Popup from "reactjs-popup";
import DemoContact from "../../components/DemoContact";
import medicalImg from "../../assets/images/medicalSvg.svg";
import targetImage from "../../assets/images/targetedEngagement.gif";
import tailoredMessaging from "../../assets/images/tailoredMessaging.gif";
import evidence from "../../assets/images/evidence.svg";
import Footer from "../../components/Footer";
import impactTracking from "../../assets/images/impactTracking.gif";
import SolutionsTextSection from "../../components/SolutionsTextSection";
import ContactFooter from "../../components/ContactFooter";
import ContactSupport from "../../components/ContactSupport";

gsap.registerPlugin(ScrollTrigger);

const SolutionsList = [
  {
    id: 1,
    text: "Medical Strategy",
    img: medicalImg,
    title: "Map of patient need across clinical journey",
    description:
      "Our clinical and data experts have years of experience in mapping the patient journey from diagnosis to post-treatment across more than 20 diseases.",
  },
  {
    id: 2,
    text: "Targeted Engagement",
    img: targetImage,
    title: "HCPs, hospitals / clinics, plans patient need (MSL ready)",
    description:
      "Measure disparities in care in a disease down to the individual physician",
  },
  {
    id: 3,
    text: "Tailored Messaging",
    img: tailoredMessaging,
    title: "Tailored messaging based on deep profiles",
    description:
      "Tailor messaging based on profiles to bring the most impactful messages to the right audiences to influence care",
  },
  {
    id: 4,
    text: "Evidence Generation and Digital",
    img: evidence,
    title: "Generate evidence and materials to support strategy",
    description:
      "Develop evidence and digital tools to support targeted engagement strategy",
  },
  {
    id: 5,
    text: "Impact Tracking",
    img: impactTracking,
    title: "Track impact of initiatives",
    description:
      "Track the real patient impact of initiatives in real-time across initiatives",
  },
];

export default function MedicalAffairsLanding() {
  const video1ref = useRef(null);
  const isAnimationPlayed = useRef(false);
  const [currentToggle, setCurrentToggle] = useState(0);
  const [showEnded, setShowEnded] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showDemoPopup, setShowDemoPopup] = useState(false);
  const [showContactPopup, setShowContactPopup] = useState(false);
  const sliderRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    AOS.init();
    SolutionsList.forEach((item) => {
      gsap.fromTo(
        `#solution-${item.id}`,
        { clipPath: "inset(0% 0% 100% 0)" },
        {
          clipPath: "inset(0% 0% 0% 0)",
          ease: "power2.inOut",
          scrollTrigger: {
            trigger: `#solution-${item.id}`,
            start: "top 70%",
            end: "top 100%",
            scrub: 1,
            pin: false,
            pinSpacing: false,
          },
        },
      );
    });
  }, []);

  useEffect(() => {
    if (location.pathname === "/contact") {
      setShowContactPopup(true);
    }
  }, [location]);

  const settings = useMemo(
    () => ({
      dots: false,
      arrows: false,
      infinite: true,
      speed: 500,
      autoplay: false,
      autoplaySpeed: 3000,
      slidesToShow: 1,
      slidesToScroll: 1,
      afterChange: (e) => {
        setCurrentToggle(e);
      },
    }),
    [],
  );

  const handleReady = useCallback(() => {
    if (isAnimationPlayed.current) return;
    isAnimationPlayed.current = true;
    gsap.fromTo(
      `#introVideo`,
      { scale: 0 },
      { scale: 1.2, ease: "power2.inOut" },
    );
  }, []);

  const handleClick = useCallback((index) => {
    sliderRef.current?.slickGoTo(index);
  }, []);

  const handleReplay = useCallback(() => {
    setShowEnded(false);
    video1ref.current?.seekTo(0);
    setIsPlaying(true);
  }, []);

  const handleEnd = useCallback(() => {
    setShowEnded(true);
    setIsPlaying(false);
  }, []);

  const closeDemoModal = useCallback(() => {
    setShowDemoPopup(false);
  }, []);

  const closeContactModal = useCallback(() => {
    setShowContactPopup(false);
  }, []);

  return (
    <>
      <Navbar />
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-white">
        <div className="z-[1] md:py-32 py-20 mx-auto grid grid-cols-1 custom:grid-cols-2 custom:px-20 px-6 gap-8">
          {/* Hero content */}
          <div className="flex gap-10 px-6 md:px-10 flex-col items-center justify-center">
            <h1
              className="text-3xl text-center md:text-5xl lg:text-6xl font-bold leading-tight"
              style={{ color: '#0f172a' }}
              data-aos="zoom-y-out"
            >
              Empowering leaders in Medical Affairs to maximize impact with{" "}
              <span
                data-aos="zoom-y-out"
                data-aos-delay="150"
                style={{ 
                  background: 'linear-gradient(to right, #2563eb, #14b8a6)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}
              >
                AI + RWD
              </span>
            </h1>

            <PrimaryBtn
              onClick={() => setShowDemoPopup(true)}
              text="Request a Demo"
            />
          </div>

          {/* Demo Request Popup */}
          <Popup
            onClose={closeDemoModal}
            modal
            open={showDemoPopup}
            className="rounded-xl"
            position="center center"
          >
            <DemoContact handleClose={closeDemoModal} />
          </Popup>

          {/* Contact Support Popup */}
          <Popup
            onClose={closeContactModal}
            modal
            open={showContactPopup}
            className="rounded-xl"
            position="center center"
          >
            <ContactSupport closeContact={closeContactModal} contactPage />
          </Popup>

          {/* Video Player */}
          <div className="flex justify-center mt-12 lg:mt-16 px-4">
            <div className="w-full" style={{ maxWidth: "640px" }}>
              <div className="relative w-full" style={{ paddingTop: "56.25%" }}>
                <ReactPlayer
                  id="introVideo"
                  url="https://youtu.be/Vl-tig9inuc?si=lTzZlUAVW0dcxRoy"
                  onEnded={handleEnd}
                  width="100%"
                  height="100%"
                  className="absolute inset-0"
                  ref={video1ref}
                  playsinline
                  onReady={handleReady}
                  playing={isPlaying}
                  onPlay={() => setIsPlaying(true)}
                  controls={true}
                />
                {showEnded && (
                  <div
                    onClick={handleReplay}
                    className="absolute left-0 top-0 w-full h-full flex items-center justify-center bg-black/50"
                  >
                    <MdOutlineReplay size={80} className="cursor-pointer text-white" />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Solutions Slider Section */}
      <section className="relative overflow-hidden md:py-20 py-10 bg-gray-50">
        {/* Solution Tabs */}
        <div className="flex flex-none md:flex-wrap flex-nowrap overflow-x-auto justify-center md:gap-4 gap-2 items-center md:px-10 px-4 mb-10 md:mb-0 py-4">
          {SolutionsList.map((item, index) => (
            <button
              key={item.id}
              onClick={() => handleClick(index)}
              className="font-medium md:text-xl text-sm rounded-lg text-center px-4 py-2 cursor-pointer transition-all"
              style={currentToggle === index 
                ? { backgroundColor: '#0f172a', color: '#ffffff', border: '1px solid #0f172a' }
                : { backgroundColor: '#ffffff', color: '#374151', border: '1px solid #e5e7eb' }
              }
            >
              {item.text}
            </button>
          ))}
        </div>

        {/* Slider Content */}
        <div className="md:px-20 px-10 h-full justify-center flex flex-col relative">
          <Slider ref={sliderRef} {...settings}>
            {SolutionsList.map((item) => (
              <div key={item.id} className="md:px-4 px-0 py-10 w-full">
                <div className="flex flex-col md:flex-row items-center md:justify-between h-full justify-center gap-10">
                  <div className="flex h-full justify-center flex-col items-center gap-4 flex-1">
                    <h3 
                      className="md:text-3xl text-xl font-semibold text-center"
                      style={{ color: '#0f172a' }}
                    >
                      {item.title}
                    </h3>
                    <p 
                      className="md:text-xl text-base text-center max-w-lg"
                      style={{ color: '#475569' }}
                    >
                      {item.description}
                    </p>
                  </div>
                  <div className="flex-1 flex justify-center">
                    <img
                      src={item.img}
                      className="md:h-80 h-48 object-contain"
                      alt={item.title}
                    />
                  </div>
                </div>
              </div>
            ))}
          </Slider>
        </div>
      </section>

      <SolutionsTextSection />
      <ContactFooter />
      <Footer />
    </>
  );
}
